import { FormHTMLAttributes, useState } from 'react'
import { useFormik } from 'formik'
import parse from 'html-react-parser'

import Button from '@/components/base/button'
import FormControl from '@/components/base/form-control'
import Spinner from '@/components/base/spinner'
import Typography from '@/components/base/typography'
import { convertJsontoFormData, createInitialValues, createYupSchema } from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import SVGIcon from './svg-icon'
import { cn } from '@/lib/utils'
import { WPGravityForm, WPGravityFormField } from '@/types'

export type FormProps = FormHTMLAttributes<HTMLFormElement> &
  Partial<{
    form_id: number
    submit: string
    fields: WPGravityForm['fields']
    theme: 'light' | 'dark'
  }>

const Form = ({ form_id, fields = [], submit, className, theme, ...props }: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('&nbsp;')

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema: createYupSchema(fields, 'it'),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const page_url_id = fields.find((field) => field.label === 'page_url')?.id
      const result = sendContactForm({
        ...values,
        ...(page_url_id ? { [`input_${page_url_id}`]: window.location.href } : {})
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
        })
        .then(() => {
          setTimeout(() => {
            resetForm()
            setStatusMessage('&nbsp;')
            setStatus('')

            setSubmitting(false)
          }, 3000)
        })
    }
  })

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      className={cn(['grid grid-cols-2 gap-spacing-xs', className])}
      {...props}
    >
      {fields?.map((field: WPGravityFormField, index) => (
        <FormControl
          key={index}
          id={`input_${field.id}`}
          name={`input_${field.id}`}
          type={field.type}
          label={field.type === 'consent' ? field.checkboxLabel : ''}
          className="col-span-2"
          required={field.isRequired}
          options={field.choices}
          size={field.size as any}
          // placeholder={`${field.placeholder}`}
          placeholder={
            (field.type === 'consent' ? field.checkboxLabel : field.label) +
            (field.isRequired ? ' *' : '')
          }
          checked={formik.values[`input_${field.id}`] || false}
          value={formik.values[`input_${field.id}`]}
          disabled={formik.isSubmitting}
          onChange={(e) => {
            formik.handleChange(e)
            if (field.type === 'consent') {
              formik.setFieldValue(`input_${field.id}_1`, e.target.checked ? '1' : '0', false)
            }
            if (field.type === 'select') {
              formik.setFieldValue(`input_${field.id}`, e, false)
            }
          }}
          onBlur={formik.handleBlur}
          error={
            formik.errors[`input_${field.id}`] !== undefined &&
            formik.touched[`input_${field.id}`] !== undefined
          }
          theme={theme}
        />
      ))}

      <div className="col-span-2">
        <Button
          type="submit"
          variant="primary"
          disabled={
            formik.isSubmitting ||
            (Object.values(formik.errors).length > 0 && formik.submitCount > 0)
          }
          className={cn('w-full', {
            'group pointer-events-none':
              formik.isSubmitting ||
              (Object.values(formik.errors).length > 0 && formik.submitCount > 0)
          })}
          endIcon={
            <SVGIcon symbol="arrow-right" className={cn('h-[0.8rem] w-[2.5rem] fill-current')} />
          }
        >
          <Typography variant="body" mask>
            {submit}
          </Typography>
          {formik.isSubmitting ? <Spinner variant="small" /> : null}
        </Button>
      </div>

      {statusMessage !== '' ? (
        <div
          className={cn([
            'text-regular flex',
            {
              'text-text-primary-light': formik.status !== 'error' && theme === 'light',
              'text-text-primary-dark': formik.status !== 'error' && theme === 'dark',
              'text-error': formik.status === 'error'
            }
          ])}
        >
          {parse(statusMessage)}
        </div>
      ) : null}
    </form>
  )
}

export default Form
